<template>
    <div>
        <label v-if="!tableView" class="form-label d-flex align-items-center flex-wrap">
            {{ fieldData['title_' + getLang()] }}
            <span class="input-group__required" v-if="fieldData.required">*</span>

            <info-label
                v-if="fieldData.settings && fieldData.settings.tooltip"
                :text="fieldData.settings.tooltip"
            />
        </label>

        <input
            v-if="fieldData.type === 'phone'"
            type="tel"
            :name="fieldData.code"
            :id="fieldData.code"
            v-model="phoneMasked"
            inputmode="tel"
            :placeholder="phonePlaceholder"
            maxlength="18"
            @keydown="handleKeydown"
            @paste="handlePaste"
            @input="handleInputSanitize"
            :class="inputClass"
            :disabled="!isEditible"
        />

        <input
            v-else-if="fieldData.type !== 'number'"
            :type="fieldData.type === 'date' ? 'date' : 'text'"
            :name="fieldData.code"
            :id="fieldData.code"
            v-model="fieldValue"
            ref="inputField"
            :maxlength="isIinBin ? 12 : null"
            @keydown="handleKeydown"
            @input="handleInputSanitize"
            @paste="handlePaste"
            :class="inputClass"
            :disabled="!isEditible"
        >

        <input
            v-else
            type="number"
            step="any"
            v-model="numberValue"
            :name="fieldData.code"
            @keydown="handleKeydown"
            @paste="handlePaste"
            :class="inputClass"
            :disabled="!isEditible"
            @wheel.prevent
        />

        <div v-if="!tableView && validationErrors">
            <span v-for="error in validationErrors" :key="error" class="error-text d-block">
                {{ error }}
            </span>
        </div>
    </div>
</template>

<script>
import InfoLabel from '../ui/InfoLabel.vue';

export default {
    name: 'text-field',
    components: {InfoLabel},
    props: {
        field: Object,
        isEditible: Boolean,
        oldData: [String, Number],
        modelValue: [String, Number],
        tableView: Boolean,
        validationErrors: Object,
    },

    data() {
        return {
            fieldData: {},
        };
    },

    computed: {
        inputClass() {
            return this.tableView
                ? 'table-input w-100'
                : (this.validationErrors ? 'form-control-1 border-danger mb-1' : 'form-control-1');
        },

        isIinBin() {
            return this.fieldData.type === 'iinbin' || ['iin', 'bin'].includes(this.fieldData.code);
        },

        isStrictNumber() {
            return this.isIinBin || this.fieldData.type === 'phone';
        },

        fieldValue: {
            get() {
                return this.modelValue;
            },
            set(value) {
                if (this.isStrictNumber && value) {
                    value = value.replace(/\D/g, '');
                }
                this.$emit('update:modelValue', value);
            },
        },

        numberValue: {
            get() {
                return this.modelValue;
            },
            set(value) {
                if (value === '' || value === null) {
                    this.$emit('update:modelValue', null);
                } else {
                    this.$emit('update:modelValue', value);
                }
            },
        },

        phoneMasked: {
            get() {
                let digits = (this.modelValue || '').toString().replace(/\D/g, '');

                if (digits.startsWith('8')) {
                    digits = '7' + digits.slice(1);
                }

                if (digits.length && !digits.startsWith('7')) {
                    digits = '7' + digits;
                }

                digits = digits.slice(0, 11);
                if (!digits) return '';

                const body = digits.slice(1);
                const p1 = body.slice(0, 3);
                const p2 = body.slice(3, 6);
                const p3 = body.slice(6, 8);
                const p4 = body.slice(8, 10);

                let result = '+7';

                if (p1) result += ` (${p1}${p1.length === 3 ? ')' : ''}`;
                if (p2) result += ` ${p2}`;
                if (p3) result += `-${p3}`;
                if (p4) result += `-${p4}`;

                return result;
            },

            set(value) {
                let digits = (value || '').toString().replace(/\D/g, '');

                if (digits.startsWith('8')) {
                    digits = '7' + digits.slice(1);
                }

                if (digits.length && !digits.startsWith('7')) {
                    digits = '7' + digits;
                }

                digits = digits.slice(0, 11);
                this.$emit('update:modelValue', digits);
            },
        },

        phonePlaceholder() {
            return '+7 (___) ___-__-__';
        },
    },

    mounted() {
        this.fieldData = this.field;

        if (this.modelValue === undefined) {
            this.$emit('update:modelValue', this.oldData ?? '');
        }

        if (this.oldData != null) {
            this.$emit('update:modelValue', this.oldData);
        }
    },

    methods: {
        getLang() {
            return document.querySelector('html')?.getAttribute('lang') || 'ru';
        },

        handleKeydown(event) {
            const controlKeys = [
                'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'Home', 'End',
            ];

            if (controlKeys.includes(event.key)) return;
            if (event.ctrlKey || event.metaKey) return;

            if (!this.isStrictNumber && this.fieldData.type !== 'number') {
                return;
            }

            if (this.isStrictNumber) {
                if (!/^[0-9]$/.test(event.key)) {
                    event.preventDefault();
                }
                return;
            }

            if (this.fieldData.type === 'number') {
                if (['e', 'E', '+', '-'].includes(event.key)) {
                    event.preventDefault();
                    return;
                }

                if (/^[0-9]$/.test(event.key)) return;

                if (['.', ','].includes(event.key)) {
                    const currentValue = String(this.modelValue || '');
                    if (currentValue.includes('.') || currentValue.includes(',')) {
                        event.preventDefault();
                    }
                    return;
                }

                event.preventDefault();
            }
        },

        handlePaste(event) {
            if (this.isStrictNumber || this.fieldData.type === 'number') {
                const text = (event.clipboardData || window.clipboardData).getData('text');

                const regex = this.isStrictNumber
                    ? /^\d+$/
                    : /^[0-9.,]+$/;

                if (!regex.test(text)) {
                    event.preventDefault();
                }
            }
        },

        handleInputSanitize(event) {
            if (this.isStrictNumber) {
                const val = event.target.value;
                const newVal = val.replace(/\D/g, '');

                if (val !== newVal) {
                    this.fieldValue = newVal;

                    if (this.fieldData.type !== 'phone') {
                        event.target.value = newVal;
                    }
                }
            }
        },
    },
};
</script>
